/**
 * Trashcan Block Schema
 *
 * Papierkorb des Dokuments: jeder ganz gelöschte Block und jedes gelöschte
 * Canvas-Element als Snapshot (keine echten Children).
 *
 * @module schemas/trashcan-model
 */

import {
  BlockModel,
  BlockSchemaExtension,
  defineBlockSchema,
} from '@blocksuite/store';

// =============================================================================
// Types
// =============================================================================

/** Where a deleted block stood: restoring puts it back there. */
export interface TrashcanItemPosition {
  parentId: string;
  /** The sibling right before it; null when it was the first child. */
  previousSiblingId: string | null;
}

/**
 * Ein gelöschtes Item im Trashcan
 */
export interface TrashcanItem {
  /** Original Block- oder Element-ID */
  id: string;
  /**
   * Weg des Eintrags: Datenbank, Automation und ELSTER haben eigene Schritte;
   * `block` ist jeder andere Block, `element` ein Canvas-Element.
   */
  blockType: 'database' | 'automation' | 'elster' | 'block' | 'element';
  /** Flavour des Blocks bzw. Typ des Canvas-Elements (Miniatur, Name) */
  kind?: string;
  /** Block: SliceSnapshot; Canvas-Element: seine serialisierten Props */
  snapshot: unknown;
  /** Timestamp wann gelöscht wurde */
  deletedAt: number;
  /** Nutzer-ID, die gelöscht hat (fehlt, wenn der Editor keinen Nutzer kennt) */
  deletedBy?: string;
  /** Optional: Name des Blocks für Anzeige */
  displayName?: string;
  /** Alte Stelle eines Blocks */
  position?: TrashcanItemPosition;
  /** Mindmap: ihre serialisierten Knoten, die mit ihr gingen und mit ihr zurückkommen */
  members?: Record<string, unknown>[];
  /** Canvas-Verbindungen, die mit dem Eintrag gingen und mit ihm zurückkommen */
  connectors?: Record<string, unknown>[];
  /** Canvas-Gruppe je Mitglied (Eintrag und seine Verbindungen), in die es zurückkehrt */
  groups?: Record<string, string>;
  /** Optional owner marker of the deleted block */
  managedBy?: string;
  /** Sharded database identity: logical database id (database items only) */
  databaseId?: string;
  /** Sharded database identity: root system document id (database items only) */
  databaseRootDocId?: string;
  /**
   * Set by the server when it moved the block in one commit with the block's
   * server state (tables, automations): such an entry comes back and leaves
   * only through the server.
   */
  appLifecycle?: {
    operationId: string;
    parentId: string;
    index: number;
    archiveUpdate: string;
  };
}

/**
 * Trashcan Block Props
 */
export type TrashcanBlockProps = {
  /** Liste der gelöschten Items */
  items: TrashcanItem[];
};

// =============================================================================
// Model
// =============================================================================

export class TrashcanBlockModel extends BlockModel<TrashcanBlockProps> {
  /**
   * Anzahl der Items im Trashcan
   */
  get itemCount(): number {
    return this.props.items.length;
  }

  /**
   * Finde Item by ID
   */
  getItem(id: string): TrashcanItem | undefined {
    return this.props.items.find(item => item.id === id);
  }

  /**
   * Alle Database-Items
   */
  get databaseItems(): TrashcanItem[] {
    return this.props.items.filter(item => item.blockType === 'database');
  }

  /**
   * Alle Automation-Items
   */
  get automationItems(): TrashcanItem[] {
    return this.props.items.filter(item => item.blockType === 'automation');
  }
}

// =============================================================================
// Schema
// =============================================================================

export const TrashcanBlockSchema = defineBlockSchema({
  flavour: 'affine:trashcan',
  props: (): TrashcanBlockProps => ({
    items: [],
  }),
  metadata: {
    role: 'hub',
    version: 1,
    // Like the server, the page keeps its trash in the document part.
    documentPart: true,
    parent: ['@root'],
    children: [], // Keine Children - nur Daten!
  },
  toModel: () => new TrashcanBlockModel(),
});

// =============================================================================
// Extension
// =============================================================================

export const TrashcanBlockSchemaExtension =
  BlockSchemaExtension(TrashcanBlockSchema);
